import Title from "antd/lib/typography/Title";
import {Result, notification, Button, Radio, Divider} from "antd";
import {useContext, useState} from "react";
import {useIntl} from "react-intl";
import {Customer, Quote, ShippingDestination} from "../../api/models";
import {ConfiguratorContext} from "../../context";
import Utils from "../../util/util";
import BMButton, {BMButtonProps} from "../BMButton";
import QuoteReviewDetail from "../QuoteReviewDetail";
import { useQuoteContext } from "../../contexts/QuoteContext";
import ModalWizard from "../ModalWizard";
import { SelectShippingDestination } from "../ShippingDestinationModal";
import { SelectEndCustomer } from "../customer_entry";
import { WizardInstance } from "../Wizard";
import StatesSelector from "../StatesSelector";
import useQuoteReview from "../../swr/useQuoteReview";
import useQuote from "../../swr/useQuote";
import {defaultPilotInspectionFeeValues, PilotInspectionFeeForm} from "./PilotInspectionModal";
import {SaveDto} from "../../api";
import {useForm} from "antd/es/form/Form";
import {ValidateErrorEntity} from "rc-field-form/lib/interface";

const SubmitQuoteButton = (props:Omit<BMButtonProps, 'onChange'> & {
  onQuoteChanged: (s:SaveDto) => void
  onDone: (q:Quote) => void
  isReservation?:boolean
}) => {

  const { onDone:a, isReservation:c, ...btnProps } = props;

  const {quote, adminView } = useQuoteContext();
  const quoteAsync = useQuote({ quoteId: quote?.quoteId, revision: quote?.revision });

  const configurator = useContext(ConfiguratorContext);
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedStateRegistration, setSelectedStateRegistration] = useState<string>();
  const [selectedShippingDestination, setSelectedShippingDestination] = useState<ShippingDestination>();
  const [selectedEndCustomer, setSelectedEndCustomer] = useState<Customer>();
  const [addPilotInspectionFee, setAddPilotInspectionFee] = useState<boolean>();
  const [initialValue, setInitialValue] = useState<Quote>();

  const [pilotInspectionFeeForm] = useForm();

  const review = useQuoteReview({
    quoteRevisionId: quote?.displayRevisionId,
    options: {
      dealerView: !adminView
    }
  })

  const handleOpen = (open:boolean) => {

    if(open) {
      setInitialValue( quote && {...quote});
      setSelectedStateRegistration(quote?.stateRegistration);
      setSelectedShippingDestination(quote?.shippingDestination);
      setSelectedEndCustomer(quote?.endCustomer);

      setAddPilotInspectionFee(false);
    }
  };

  const submit = async () : Promise<Quote|undefined> => {
    if (!quote) return;

    try {
      const options = {
        reservation: props.isReservation
      };
      const resp = await configurator.api.submitQuoteApproval(quote.displayRevisionId, options );

      notification["success"]({
        message: "Submitted",
        description: "Your quote has been submitted for approval",
      });

      setIsOpen(false);
      return resp.data;
    } catch (e: any) {
      console.log(e);
      const id = e.response?.data?.message || e.message ;
      const errorMsg = intl.formatMessage({ id });
      const msg = "Failed to submit quote. " + errorMsg
      notification.error( { message: msg, duration: 0 });
    }

    return;
  };

  const handleOpenBtn = async () => {

    const needsReview = props.isReservation || Utils.reviewHasErrors(review.data) || !quote?.shippingDestination || !quote.stateRegistration || !quote.endCustomer
    const needsPilotInspectionFeeDetails = !quote?.pilotInspectionFee;
    if ( needsReview || needsPilotInspectionFeeDetails ) {
      setIsOpen( true );
    }
    else {
      handleSubmit();
    }
  }

  const isLastStep = (nav:WizardInstance) => {
    return nav.getLastStep()?.key == nav.getCurrentStep()?.key;
  }

  const nextStep = (nav:WizardInstance) => {

    if ( isLastStep(nav) ) {
      handleConfirmSubmitBtn(nav);
    }
    else {
      nav.nextStep();
    }
  }

  const handleChangePilotInspectionFee= async (nav:WizardInstance) => {

    //if inspection is not required, continue
    if (!addPilotInspectionFee) {
      nextStep(nav);
      return;
    }

    //else validate and update quote
    if (!quote) return;

    try {

      const pilotInspectionFee = await pilotInspectionFeeForm.validateFields();

      await props.onQuoteChanged({
        pilotInspectionFee: pilotInspectionFee
      })

      nextStep(nav);
    }
    catch(e:any) {
      const validationErrors = e as ValidateErrorEntity;
      const errs = validationErrors.errorFields.map( f => f.errors ).flat().map( (msg, ndx) => <li key={`validationError-${ndx}`}>{msg}</li> );
      const errorMsg = !errs.length ? "Please fix validation errors." : <ul>{errs}</ul>;
      notification.error({message: errorMsg });
    }
  }

  const handleSelectCustomer = async (nav:WizardInstance) => {
    if (!quote) return;

    if ( !selectedEndCustomer ) return;

    await props.onQuoteChanged({
      endCustomerId: selectedEndCustomer.id
    });

    nextStep(nav);
  }

  const handleSelectDestination =  async (nav:WizardInstance) => {
    if (!quote) return;

    if ( !selectedShippingDestination ) return;

    await props.onQuoteChanged({
      shippingDestination: selectedShippingDestination.id
    });

    nextStep(nav);
  }

  const handleSelectStateRegistration = async (nav:WizardInstance) => {
    if (!quote) return;

    if ( !selectedStateRegistration ) return;

    await props.onQuoteChanged({
      stateRegistration: selectedStateRegistration
    });

    nextStep(nav);
  }

  const handleConfirmSubmitBtn = async (nav:WizardInstance) => {
    handleSubmit();
  }

  const handleSubmit = async () => {
    const quote = await submit();
    if (quote) {
      props.onDone?.(quote);
      setIsOpen(false);
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
  }

  const getInvalidStateRegistrationMsg = (v:string | undefined) : string | undefined => {
    return !v ? "Please select a state registration before continuing."
        : undefined;
  }


  const isWorking = quoteAsync?.isLoading || review.isLoading;

  const btnTitle = props.isReservation ? "Submit as Reservation" : "Submit Quote";

  return <>
    <BMButton
      type="primary"
      onClick={handleOpenBtn}
      {...btnProps}
      loading={isWorking}
    > {btnTitle}</BMButton>
    <ModalWizard
      centered
      open={isOpen}
      onCancel={handleCancel}
      style={{
        minWidth: "50rem",
        width: "fit-content"
      }}
      showSteps={false}
      afterOpenChange={handleOpen}
      steps={[
        {
          key:"reviewQuote",
          hidden: !Utils.reviewHasErrors(review.data),
          body:(_nav) => <Result status="warning" 
            title={"The following issues with the quote have been identified."}
            subTitle={<Title level={5}>Submit for approval?</Title> }
          >
            <QuoteReviewDetail 
              review={review.data}
              ignoreDashDrawing={true} 
            />
          </Result>,
          footer: (nav) => <div style={{display: "flex", gap: ".5rem", flexDirection: "row-reverse", padding: "1rem .3rem .3rem .3rem" }}>
            <Button key="next" type="primary" loading={isWorking} onClick={() => nextStep(nav)} >{isLastStep(nav) ? "Submit" : "Next"}</Button>
            <Button key="back" onClick={handleCancel} >Cancel</Button>
          </div>,
        },
        {
          key:"stateRegistration",
          hidden: !!initialValue?.stateRegistration,
          body:(_nav) => <div>
            <div style={{display:"flex", flexDirection: "column", marginTop: "2rem", alignItems: "center", gap: "1rem"}} >
            <Title level={5}>Please select the state to be registered.</Title>
            <StatesSelector includeTbd={true} allowClear={false} value={selectedStateRegistration} onChange={setSelectedStateRegistration} style={{width: "10rem"}} />
            </div>
          </div>,
          footer: (nav) => <div style={{display: "flex", gap: ".5rem", flexDirection: "row-reverse", padding: "1rem .3rem .3rem .3rem" }}>
            <BMButton key="next" type="primary"
                      disabled={!!getInvalidStateRegistrationMsg(selectedStateRegistration)}
                      onDisabledClick={() => Utils.notifyDisabled(getInvalidStateRegistrationMsg(selectedStateRegistration))}
                      loading={isWorking}
                      onClick={() => handleSelectStateRegistration(nav)} >{isLastStep(nav) ? "Submit" : "Next"}</BMButton>
            <Button key="cancel" type="primary" onClick={handleCancel} >Cancel</Button>
          </div>,
        },
        {
          key:"selectShippingDestination",
          hidden: !!initialValue?.shippingDestination,
          body:(_nav) => <div>
            <Title level={5}>A shipping destination is required.</Title>
            <SelectShippingDestination value={selectedShippingDestination} onChange={setSelectedShippingDestination} />
          </div>,
          footer: (nav) => <div style={{display: "flex", gap: ".5rem", flexDirection: "row-reverse", padding: "1rem .3rem .3rem .3rem" }}>
            <Button key="next" type="primary" disabled={!selectedShippingDestination} loading={isWorking} onClick={() => handleSelectDestination(nav)} >{isLastStep(nav) ? "Submit" : "Next"}</Button>
            <Button key="cancel" type="primary" onClick={handleCancel} >Cancel</Button>
          </div>,
        },
        {
          key:"selectCustomer",
          hidden: !!initialValue?.endCustomer,
          body:(_nav) => <div>
            <Title level={5}>A customer is required.</Title>
            <SelectEndCustomer value={selectedEndCustomer} onChange={setSelectedEndCustomer} />
          </div>,
          footer: (nav) => <div style={{display: "flex", gap: ".5rem", flexDirection: "row-reverse", padding: "1rem .3rem .3rem .3rem" }}>
            <Button key="next" type="primary" disabled={!selectedEndCustomer} loading={isWorking} onClick={() => handleSelectCustomer(nav)} >{isLastStep(nav) ? "Submit" : "Next"}</Button>
            <Button key="cancel" type="primary" onClick={handleCancel} >Cancel</Button>
          </div>,
        },
        {
          key:"pilotInspectionFee",
          hidden: !!initialValue?.pilotInspectionFee,
          body:(_nav) => <div>
            <div style={{display:"flex", flexDirection: "column", marginTop: "2rem", alignItems: "center", gap: "1rem"}} >
              <Title level={5}>Is a pilot inspection at the factory required?</Title>
              <Radio.Group value={addPilotInspectionFee} onChange={(e) => setAddPilotInspectionFee( e.target.value) } >
                <Radio value={true} style={{marginBottom: ".5rem"}}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Radio.Group>

              {addPilotInspectionFee && <>
                <div>

                  <Divider  />

                  <PilotInspectionFeeForm
                      form={pilotInspectionFeeForm}
                      initialValues={quote?.pilotInspectionFee || defaultPilotInspectionFeeValues}
                      quantity={quote?.quantity || 0}
                  />
                </div>
              </>}
            </div>
          </div>,
          footer: (nav) => <div style={{display: "flex", gap: ".5rem", flexDirection: "row-reverse", padding: "1rem .3rem .3rem .3rem" }}>
            <BMButton key="next" type="primary"
                      loading={isWorking}
                      onClick={() => handleChangePilotInspectionFee(nav)} >{isLastStep(nav) ? "Submit" : "Next"}</BMButton>
            <Button key="cancel" type="primary" onClick={handleCancel} >Cancel</Button>
          </div>,
        },
        {
          key:"setReservation",
          hidden: !props.isReservation,
          body:(_nav) => <Result status="warning" 
            title={"This is a reservation."}
            subTitle={<Title level={5}>Submit for approval?</Title> }
          >
            <ul>
              <li>This is a reservation for future production capacity.</li>
              <li>It requires approval before it will take effect.</li>
              <li>It must be converted into a quote to become an order.</li>
            </ul>
          </Result>,
          footer: (nav) => <div style={{display: "flex", gap: ".5rem", flexDirection: "row-reverse", padding: "1rem .3rem .3rem .3rem" }}>
            <Button key="next" type="primary" loading={quoteAsync?.isLoading} onClick={() => handleConfirmSubmitBtn(nav)} >Submit</Button>
            <Button key="back" onClick={handleCancel} >Cancel</Button>
          </div>,
        }
      ]}
    />
  </>
}

export default SubmitQuoteButton;
